import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { noticesFor, useNotices } from '../data/courseStatus.ts'
import { useAssets, type AssetRow } from '../data/fleet.ts'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { settingLines, usePlan } from '../data/plan.ts'
import { areaShort, areasSummary, longDay, machineLabel, useNames } from '../data/planUi.ts'
import { localDate, formatLocal, ageMs } from '../lib/time.ts'
import { NoticeLine } from '../ui/NoticeLine.tsx'
import { SettingLinesView, frameOfAreas } from './plan/JobSettings.tsx'
import { groupName } from './plan/shared.ts'

/** The board for a screen in the shed (display mode): today's jobs in large type, who is on which machine and where they start, nothing to tap.
 *  It reads the device's own copy, so it keeps showing the last plan when the network drops, and says how old that plan is. It shows no times: the
 *  order of the day is first jobs, second jobs. It rolls over to the next day by itself at midnight (site time).
 *  M16-B1: the course notices in force today come first, in large type (a competition and its first start, a green closed), from the same copy. */
export function PlanDisplayPage() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const st = useEngineState()
  const lang = i18n.language
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => { const id = window.setInterval(() => setNow(Date.now()), 30_000); return () => window.clearInterval(id) }, [])
  const date = localDate(now, site.timezone)
  const { ready, plan, taskTypes } = usePlan(lang)
  const names = useNames(); const areas = useAreas(); const assets = useAssets(); const notices = useNotices()
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  const assetById = useMemo(() => new Map((assets ?? []).map((a) => [a.id, a as AssetRow])), [assets])
  if (!ready || !names || !areas || !assets) return <p className="p-6 text-2xl">{t('common.loading')}</p>
  const todayNotices = noticesFor(notices, date)
  const jobs = plan.day(date).filter((j) => j.state !== 'done')
  const groups = [...new Set(jobs.map((j) => j.sequence_group))].sort((a, b) => a - b)
  const stale = st.lastSyncAt !== null && ageMs(st.lastSyncAt, now) > 15 * 60_000
  return (
    <main className="flex min-h-dvh flex-col gap-6 bg-slate-900 p-6 text-white" data-testid="plan-tv">
      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="text-5xl font-bold first-letter:uppercase" data-testid="tv-date">{longDay(date, lang)}</h1>
        <p className="text-2xl opacity-80">{site.name}{st.lastSyncAt ? ` · ${t('plan.tvUpdated', { when: formatLocal(st.lastSyncAt, site.timezone, 'HH:mm') })}` : ''}</p>
      </header>
      {stale && <p className="rounded-xl bg-amber-500 p-3 text-2xl font-semibold text-black" data-testid="tv-stale">{t('plan.tvStale')}</p>}
      {todayNotices.length > 0 && (
        <section className="flex flex-col gap-3" data-testid="tv-notices" aria-label={t('courseStatus.tvTitle')}>
          {todayNotices.map((n) => <NoticeLine key={n.id} notice={n} areaById={areaById} lang={lang} size="lg" testId="tv-notice" />)}
        </section>
      )}
      {jobs.length === 0 && <p className="text-4xl opacity-80" data-testid="tv-empty">{t('plan.empty')}</p>}
      {groups.map((g) => (
        <section key={g} className="flex flex-col gap-3" data-testid="tv-group">
          <h2 className="text-3xl font-bold text-emerald-300">{groupName(t, g)}</h2>
          <ul className="grid gap-4 lg:grid-cols-2">
            {jobs.filter((j) => j.sequence_group === g).map((j) => (
              <li key={j.id} className="flex flex-col gap-2 rounded-2xl border-2 border-slate-600 bg-slate-800 p-5" data-testid="tv-job">
                <p className="text-4xl font-bold">{j.title_text}</p>
                <p className="text-2xl opacity-90">{areasSummary(j.areaIds, areaById, lang, t)}</p>
                {Object.keys(j.params).length > 0 && <SettingLinesView lines={settingLines(j.params, taskTypes.find((x) => x.code === j.task_type_code), lang)} frame={frameOfAreas(j.areaIds, areaById)} size="tv" pictureClass="text-sky-300" className="text-2xl text-sky-300" testId="tv-params" />}
                <ul className="mt-1 flex flex-col gap-1">
                  {j.crew.map((a) => <li key={a.id} className="text-3xl"><strong>{names.get(a.person_id) ?? '?'}</strong>{a.asset_id ? ` — ${machineLabel(assetById.get(a.asset_id))}` : ''}{a.start_area_id ? ` → ${areaShort(areaById.get(a.start_area_id), areaById, lang, t)}` : ''}</li>)}
                  {j.crew.length === 0 && <li className="text-2xl italic opacity-70">{t('plan.nobody')}</li>}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {/* a TV, not a glove: 44 px is enough for the one control of the screen (M23 item 1) */}
      <footer className="mt-auto flex justify-end"><Link to="/plan" className="btn !min-h-11 text-lg" data-testid="tv-exit">{t('plan.tvExit')}</Link></footer>
    </main>
  )
}
